/**
 * useRedirectIfPwaLaunch — bounce a marketing page to the in-app
 * surface when the user opens us in a standalone PWA window.
 *
 * Why: iOS Safari prior to 16.4 (and a handful of edge cases in modern
 * browsers when Add-to-Home-Screen is invoked from a non-start_url
 * surface) ignores the manifest's `start_url` and launches the PWA at
 * the URL the user happened to be on when they installed. If a golfer
 * installs us from golfauth.com/ they'd open the PWA into the
 * marketing page on every launch — wrong shape for an "installed app"
 * mental model.
 *
 * Defensive fix: on the marketing surfaces (HomePage at /, ProPage at
 * /pro), detect "we're running standalone" on first paint and replace
 * the route with the appropriate in-app start url. The protected
 * routes downstream handle the sign-in redirect.
 *
 * Three signals are checked because the PWA-launch detection story
 * differs by browser:
 *   - matchMedia('(display-mode: standalone)')   → modern Chrome /
 *     Android / desktop installed PWAs.
 *   - matchMedia('(display-mode: fullscreen)')   → some Chrome
 *     standalone configurations + Android WebAPK.
 *   - (navigator as any).standalone === true     → iOS Safari Home
 *     Screen launch (Apple's pre-standard property).
 *
 * Any of the three → bounce. False positives are harmless: a regular
 * browser session won't have any of them set.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/* eslint-disable @typescript-eslint/no-explicit-any */

export function useRedirectIfPwaLaunch(target: string) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Only bounce on the first paint for this route — avoid an
    // infinite loop if the target also mounts a PWA redirector.
    if (location.pathname === target) return;

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.matchMedia?.('(display-mode: fullscreen)')?.matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      navigate(target, { replace: true });
    }
  }, [navigate, location.pathname, target]);
}
